'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FUEL_COLORS, FUEL_LABELS, type FuelType } from '@/constants/fuel-colors';
import type { DailyPriceResponse, PriceHistoryItem, RegionPriceResponse } from '@/lib/api/data-contracts';
import { apiClient } from '@/lib/api-client';
import { FuelStationApiService, hasLocation, type LocatedStation, type PetroleumStation } from '@/lib/fuel-station-api';
import { loadStationSnapshot } from '@/lib/station-snapshot';
import { format } from 'date-fns';
import L, { Map as LeafletMap } from 'leaflet';
import 'leaflet.markercluster';
import {
  BarChart3,
  Bath,
  Building2,
  Car,
  CircleDollarSign,
  ChevronRight,
  Clock,
  Expand,
  Filter,
  Fuel,
  Loader2,
  MapPin,
  MapPinned,
  MapPinOff,
  Minus,
  Phone,
  Plus,
  Search,
  Star,
  Store,
  TrendingUp,
  Wrench,
  X
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type TouchEvent } from 'react';
import { MapContainer, ScaleControl, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import { KoreaRegionLayer, type RegionInfo } from './korea-region-layer';
import { geoSigunguToOpinet, needsGuPolygonFilter } from '@/lib/region-code-mapper';
import { isPointInGeometry } from '@/lib/geo-utils';
import { StatisticsLegend } from './statistics-legend';
import { PriceHeatLayer } from './price-heat-layer';
import { StoreSalesLayer, type StoreSalesPoint } from './store-sales-layer';
import { SalesLegend } from './sales-legend';
import {
  applyDailyPrices,
  findOutliers,
  getPrice,
  groupDailyPrices,
  PRICE_RAMP,
  recentDates,
  regionAverages,
  type HeatLevel,
} from '@/lib/price-analysis';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  fetchSidoSummaries,
  fetchSigunguSummaries,
  indexByAdmCd,
  type CensusSummary,
} from '@/lib/sgis-api';
import {
  buildChoroplethScale,
  getMetricDef,
  getMetricValue,
  METRIC_DEFS,
  type ChoroplethScale,
  type MetricKey,
} from '@/constants/sgis-metrics';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';

/**
 * GET /me/stores 응답 — 로그인 사용자가 접근 가능한 주유소.
 * opinetId 는 주유소 관리 화면에서 등록되며 미등록이면 null 이다.
 * (생성 타입에 아직 없어 직접 선언한다 — 다음 API 타입 재생성 시 교체)
 */
interface MyStore {
  storeCd: string;
  storeName: string;
  opinetId: string | null;
}

/** 통계 오버레이 기준 연도 (가용 최신연도) */
const STATS_YEAR = 2024;

/** 가격 타임라인 기간 (서버 @Max(30)) */
const TIMELINE_DAYS = 30;

/** 이번 달 (yyyy-MM) */
const currentYearMonth = format(new Date(), 'yyyy-MM');

/** 세로 쓸어넘김 감지 (모바일 시트·상세 카드 공통). threshold 이상 움직이면 방향 콜백 */
function useSwipeY(onSwipe: (direction: 'up' | 'down') => void, threshold = 60) {
  const startY = useRef<number | null>(null);
  return {
    onTouchStart: (e: TouchEvent) => { startY.current = e.touches[0].clientY; },
    onTouchEnd: (e: TouchEvent) => {
      if (startY.current === null) return;
      const dy = e.changedTouches[0].clientY - startY.current;
      startY.current = null;
      if (Math.abs(dy) >= threshold) onSwipe(dy < 0 ? 'up' : 'down');
    },
  };
}

/** 우측 툴바 버튼 (줌·필터·지역·통계·가격 공통) */
function ToolButton({ active, title, onClick, children }: {
  active?: boolean; title: string; onClick: () => void; children: ReactNode;
}) {
  return (
    <Button size="sm" variant={active ? 'default' : 'outline'} className="shadow-md h-8 w-8 p-0" onClick={onClick} title={title}>
      {children}
    </Button>
  );
}




// Leaflet 기본 아이콘 설정 (Vite 환경용)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/leaflet/marker-icon-2x.png',
  iconUrl: '/leaflet/marker-icon.png',
  shadowUrl: '/leaflet/marker-shadow.png',
});

// 브랜드 로고 스프라이트 — marker-face.png는 35px 피치의 33x33 셀 격자(시트 263x263)
const POLL_SPRITE_SHEET = 263;
const POLL_SPRITE_CELL = 33;

// 브랜드 로고가 없는 pollCode(SKG·NHO·ETC·UNKNOWN)는 주유기 아이콘으로 폴백한다
const FUEL_PUMP_SPRITE: [number, number] = [70, 35];

// pollCode별 스프라이트 좌표
const POLL_CODE_SPRITE_MAP: Record<string, [number, number]> = {
  'SKE': [0, 140],
  'GSC': [0, 70],
  'SOL': [35, 140],
  'HDO': [105, 0],
  'E1G': [70, 0],
  'RTO': [0, 105],
  'RTX': [0, 105],
};

// pollCode 한글 브랜드명 (필터 UI용)
const POLL_CODE_BRAND_NAMES: Record<string, string> = {
  'SKE': 'SK에너지',
  'SKG': 'SK가스',
  'GSC': 'GS칼텍스',
  'SOL': 'S-OIL',
  'HDO': '현대오일뱅크',
  'E1G': 'E1',
  'NHO': '농협',
  'RTO': '알뜰',
  'RTX': '알뜰',
  'ETC': '기타',
  'UNKNOWN': '미상',
};

// 필터 패널 브랜드 선택지 (서버 필터 결과에 따라 줄어들지 않도록 고정)
const BRAND_CODES = Object.keys(POLL_CODE_BRAND_NAMES).filter(code => code !== 'UNKNOWN');

// dot + 텍스트 마커 생성 (배경 없음)
function getStationDotIcon(station: PetroleumStation, isSelected: boolean = false, colorOverride?: string): L.DivIcon {
  const name = station.name.length > 8 ? station.name.slice(0, 8) + '…' : station.name;

  const fuelEntries: { color: string; price: number }[] = [];
  if ((station.priceGasoline ?? 0) > 0)   fuelEntries.push({ color: FUEL_COLORS.gas.primary,       price: station.priceGasoline! });
  if ((station.priceDiesel ?? 0) > 0)     fuelEntries.push({ color: FUEL_COLORS.diesel.primary,     price: station.priceDiesel! });
  if ((station.pricePremiumGas ?? 0) > 0) fuelEntries.push({ color: FUEL_COLORS.premiumGas.primary, price: station.pricePremiumGas! });
  if ((station.priceKerosene ?? 0) > 0)   fuelEntries.push({ color: FUEL_COLORS.kerosene.primary,   price: station.priceKerosene! });
  if ((station.priceLpg ?? 0) > 0)        fuelEntries.push({ color: FUEL_COLORS.lpg.primary,        price: station.priceLpg! });

  // 스프라이트에서 브랜드 로고 셀을 잘라 쓴다. 표시 크기에 맞춰 시트 전체를 비례 축소한다.
  const [spriteX, spriteY] = POLL_CODE_SPRITE_MAP[station.pollCode ?? ''] ?? FUEL_PUMP_SPRITE;
  const iconSize = isSelected ? 28 : 24;
  const spriteScale = iconSize / POLL_SPRITE_CELL;
  const spriteStyle =
    `background-size:${(POLL_SPRITE_SHEET * spriteScale).toFixed(2)}px;` +
    `background-position:${(-spriteX * spriteScale).toFixed(2)}px ${(-spriteY * spriteScale).toFixed(2)}px`;

  // 테두리 대신 box-shadow 링을 쓴다 — 박스 크기가 변하지 않아 스프라이트 배율이 어긋나지 않는다
  const ring = isSelected
    ? `box-shadow:0 0 0 2px #3b82f6,0 0 0 4px rgba(59,130,246,0.35),0 1px 4px rgba(0,0,0,0.35)`
    : colorOverride
      ? `box-shadow:0 0 0 3px ${colorOverride},0 1px 4px rgba(0,0,0,0.35)`
      : '';
  const iconStyle = `${spriteStyle};${ring}`;

  const fuelHtml = fuelEntries
    .map(e => `<span class="sm-fuel-item"><span class="sm-fuel-dot" style="background:${e.color}"></span>${e.price.toLocaleString()}</span>`)
    .join('');

  return L.divIcon({
    className: 'sm-dot-marker',
    html: `
      <div class="sm-dot-wrap ${isSelected ? 'sm-dot-wrap--selected' : ''}">
        <div class="sm-icon" style="${iconStyle}"></div>
        <div class="sm-label">
          <span class="sm-label-name">${name}</span>
          ${fuelEntries.length > 0 ? `<div class="sm-label-fuels">${fuelHtml}</div>` : ''}
        </div>
      </div>
    `,
    iconSize: [0, 0],
    iconAnchor: [12, 12],
    popupAnchor: [0, -14],
  });
}

// 지도 이벤트 핸들러 컴포넌트 — 이동/줌이 끝나면 뷰포트 경계를 알린다 (최초 1회 포함)
function MapEventHandler({ onViewportChange, onZoom }: { onViewportChange: () => void; onZoom: (zoom: number) => void }) {
  const map = useMapEvents({ moveend: onViewportChange, zoomend: () => onZoom(map.getZoom()) });
  useEffect(() => {
    onViewportChange();
    onZoom(map.getZoom());
  }, [map, onViewportChange, onZoom]);
  return null;
}

// 클러스터링 마커 레이어
function MarkerClusterGroupLayer({
  stations,
  selectedStation,
  onSelect,
  priceFuel,
  priceScale,
}: {
  stations: PetroleumStation[];
  selectedStation: PetroleumStation | null;
  onSelect: (station: PetroleumStation) => void;
  priceFuel: FuelType;
  /** 지정 시 마커 색 = 가격 분위, 클러스터 = 평균가 */
  priceScale?: ChoroplethScale | null;
}) {
  const map = useMap();
  const clusterRef = useRef<L.MarkerClusterGroup | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const priceScaleRef = useRef(priceScale);
  priceScaleRef.current = priceScale;

  useEffect(() => {
    const group = (L as any).markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      iconCreateFunction: (cluster: any) => {
        const count = cluster.getChildCount();
        const scale = priceScaleRef.current;
        if (scale) {
          const prices: number[] = cluster.getAllChildMarkers()
            .map((m: any) => m.options.price as number | undefined)
            .filter((v: number | undefined): v is number => v !== undefined);
          if (prices.length > 0) {
            const avg = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
            return L.divIcon({
              html: `<div class="sm-cluster sm-cluster--price" style="background:${scale.colorFor(avg)}" title="${count}곳 평균">${avg.toLocaleString()}</div>`,
              className: '',
              iconSize: [56, 28] as L.PointExpression,
              iconAnchor: [28, 14] as L.PointExpression,
            });
          }
        }
        let cls = 'sm-cluster--small';
        let size = 32;
        if (count >= 50) { cls = 'sm-cluster--large'; size = 48; }
        else if (count >= 10) { cls = 'sm-cluster--medium'; size = 40; }
        return L.divIcon({
          html: `<div class="sm-cluster ${cls}">${count}</div>`,
          className: '',
          iconSize: [size, size] as L.PointExpression,
          iconAnchor: [size / 2, size / 2] as L.PointExpression,
        });
      },
    }) as L.MarkerClusterGroup;
    clusterRef.current = group;
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [map]);

  useEffect(() => {
    const group = clusterRef.current;
    if (!group) return;
    group.clearLayers();
    // 전국 줌에서는 1만 건 — 개별 addLayer 대신 bulk addLayers (markercluster 의 고속 경로)
    const markers = stations.filter(hasLocation).map((station) => {
      const isSelected = selectedStation?.id === station.id;
      const marker = L.marker([station.latitude, station.longitude], {
        icon: getStationDotIcon(station, isSelected, priceScale?.colorFor(getPrice(station, priceFuel))),
        zIndexOffset: isSelected ? 1000 : 0,
        price: getPrice(station, priceFuel),
      } as L.MarkerOptions);
      marker.on('click', () => onSelectRef.current(station));
      const gasPrice = (station.priceGasoline ?? 0) > 0 ? `${station.priceGasoline!.toLocaleString()}원` : '-';
      const dieselPrice = (station.priceDiesel ?? 0) > 0 ? `${station.priceDiesel!.toLocaleString()}원` : '-';
      marker.bindPopup(
        `<div style="min-width:150px;font-family:inherit">
          <div style="font-weight:600;font-size:13px;margin-bottom:3px">${station.name}</div>
          <div style="font-size:11px;color:#6b7280;margin-bottom:6px">${station.address}</div>
          <div style="font-size:11px;display:flex;gap:10px">
            <span><b>휘발유</b> ${gasPrice}</span>
            <span><b>경유</b> ${dieselPrice}</span>
          </div>
        </div>`,
        { maxWidth: 220 }
      );
      return marker;
    });
    group.addLayers(markers);
  }, [stations, selectedStation, priceFuel, priceScale]);

  return null;
}

// 주유소 검색 사이드패널
interface StationSidePanelProps {
  selectedStation: PetroleumStation | null;
  stations: PetroleumStation[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onStationSelect: (station: PetroleumStation) => void;
  onClose: () => void;
  loading?: boolean;
  open: boolean;
  /** 주유소 ID → 주변 평균 대비 가격 차(원, 음수=저렴) */
  outliers?: Map<string, number>;
  /** 모바일 시트 전체화면 여부 */
  expanded: boolean;
  /** 모바일 시트 헤더를 위/아래로 쓸어넘김 */
  onSwipe: (direction: 'up' | 'down') => void;
  /** 접근 가능한 내 주유소 목록 (2곳 이상일 때만 선택 UI를 그린다) */
  myStores: MyStore[];
  myStoreCd: string;
  onMyStoreChange: (storeCd: string) => void;
}

function StationSidePanel({
  selectedStation,
  stations,
  searchQuery,
  onSearchChange,
  onStationSelect,
  onClose,
  loading = false,
  open,
  outliers,
  expanded,
  onSwipe,
  myStores,
  myStoreCd,
  onMyStoreChange
}: StationSidePanelProps) {
  const swipe = useSwipeY(onSwipe);
  const filteredStations = stations.filter(station =>
    station.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    station.address.toLowerCase().includes(searchQuery.toLowerCase())
  );
  // 좌표가 없어 지도에 표시되지 않는 주유소 수
  const unlocatedCount = filteredStations.filter(station => !hasLocation(station)).length;
  // 전국 스냅샷 전환 후 뷰포트에 수천 건이 들어올 수 있어 목록 DOM 은 가까운 순 300개로 제한
  const listStations = filteredStations.slice(0, 300);

  if (!open) return null;

  return (
    <div className={`fixed bottom-0 left-0 z-[1001] w-full md:h-full md:w-96 md:rounded-none bg-white/95 backdrop-blur-sm border-t md:border-t-0 md:border-r shadow-xl animate-in slide-in-from-bottom md:slide-in-from-left duration-300 transition-[height] ease-out ${
      expanded ? 'h-full' : 'h-[50vh] rounded-t-2xl'
    }`}>
      <div className="flex flex-col h-full">
        {/* Header — 모바일에서는 드래그 핸들: 위로 쓸면 전체화면, 아래로 쓸면 축소/닫기 */}
        <div className="flex items-center justify-between p-4 border-b relative touch-pan-x" {...swipe}>
          <div className="md:hidden absolute top-1.5 left-1/2 -translate-x-1/2 h-1 w-10 rounded-full bg-gray-300" />
          <div className="font-semibold flex items-center gap-2">
            <Fuel className="size-5 text-blue-600" />
            주유소 검색
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>

        {/* 내 주유소 선택 — 접근 가능한 주유소가 2곳 이상일 때만 노출 */}
        {myStores.length > 1 && (
          <div className="px-4 pt-4">
            <div className="text-xs font-semibold text-gray-500 mb-1.5">내 주유소</div>
            <Select value={myStoreCd} onValueChange={onMyStoreChange}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="주유소를 선택하세요" />
              </SelectTrigger>
              <SelectContent className="z-[1100]">
                {myStores.map(store => (
                  <SelectItem key={store.storeCd} value={store.storeCd} className="text-sm">
                    {store.storeName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* 검색 입력 */}
        <div className="pt-4 px-4 space-y-2 border-b pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 size-4" />
            <Input
              type="text"
              placeholder="주유소명 또는 주소로 검색..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 pr-4"
            />
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {loading ? (
                <span className="flex items-center gap-1">
                  <Loader2 className="size-3 animate-spin" />
                  검색중...
                </span>
              ) : (
                `${filteredStations.length}개 주유소`
              )}
            </span>
            {!loading && unlocatedCount > 0 && (
              <span className="flex items-center gap-1 text-amber-600" title="좌표 정보가 없어 지도에 표시되지 않습니다">
                <MapPinOff className="size-3" />
                위치 정보 없음 {unlocatedCount}개
              </span>
            )}
          </div>
        </div>

        {/* 주유소 목록 */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="divide-y">
              {loading && stations.length === 0 ? (
                <div className="p-8 text-center">
                  <Loader2 className="size-8 animate-spin mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-500">주유소를 검색하고 있습니다...</p>
                </div>
              ) : filteredStations.length === 0 ? (
                <div className="p-8 text-center">
                  <MapPin className="size-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-500">
                    {searchQuery ? '검색 결과가 없습니다.' : '주유소를 찾을 수 없습니다.'}
                  </p>
                </div>
              ) : (
                listStations.map((station) => (
                  <div
                    key={station.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${selectedStation?.id === station.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                    onClick={() => onStationSelect(station)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{station.name}</h4>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <Badge variant={station.isOpen ? "default" : "secondary"} className="text-xs whitespace-nowrap">
                            {station.isOpen ? "영업중" : "휴업"}
                          </Badge>
                          {station.isSelf && <Badge variant="outline" className="text-xs whitespace-nowrap">셀프</Badge>}
                          {station.isLpg && <Badge variant="outline" className="text-xs whitespace-nowrap">LPG</Badge>}
                          {outliers?.has(station.id) && (
                            <Badge
                              variant="outline"
                              className={`text-xs whitespace-nowrap ${outliers.get(station.id)! < 0 ? 'border-green-600 text-green-700' : 'border-red-500 text-red-600'}`}
                            >
                              주변보다 {Math.abs(outliers.get(station.id)!).toLocaleString()}원 {outliers.get(station.id)! < 0 ? '저렴' : '비쌈'}
                            </Badge>
                          )}
                          {!hasLocation(station) && (
                            <Badge variant="outline" className="text-xs whitespace-nowrap border-amber-500 text-amber-600 gap-1">
                              <MapPinOff className="size-3" />
                              위치 정보 없음
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{station.address}</p>
                        {!hasLocation(station) && (
                          <p className="text-xs text-amber-600 mt-1">좌표 정보가 없어 지도에 표시되지 않습니다.</p>
                        )}

                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                          {(station.priceGasoline ?? 0) > 0 ? (
                            <span className="text-sm">
                              <span className="font-medium" style={{ color: FUEL_COLORS.gas.text }}>
                                {station.priceGasoline!.toLocaleString()}원
                              </span>
                              <span className="text-gray-500 ml-1">휘발유</span>
                            </span>
                          ) : null}
                          {(station.priceDiesel ?? 0) > 0 ? (
                            <span className="text-sm">
                              <span className="font-medium" style={{ color: FUEL_COLORS.diesel.text }}>
                                {station.priceDiesel!.toLocaleString()}원
                              </span>
                              <span className="text-gray-500 ml-1">경유</span>
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {hasLocation(station)
                        ? <MapPin className="size-4 text-gray-400 mt-1" />
                        : <MapPinOff className="size-4 text-amber-500 mt-1" />}
                    </div>
                  </div>
                ))
              )}
              {filteredStations.length > listStations.length && (
                <div className="p-3 text-center text-xs text-gray-500">
                  가까운 {listStations.length}개만 표시 · 지도를 확대하면 목록이 좁혀집니다
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}

// 유가 이력 차트 설정 (FUEL_COLORS 상수 사용)
const priceChartConfig = {
  gas: {
    label: '휘발유',
    color: FUEL_COLORS.gas.primary,
  },
  diesel: {
    label: '경유',
    color: FUEL_COLORS.diesel.primary,
  },
  premiumGas: {
    label: '고급휘발유',
    color: FUEL_COLORS.premiumGas.primary,
  },
  kerosene: {
    label: '등유',
    color: FUEL_COLORS.kerosene.primary,
  },
  lpg: {
    label: 'LPG',
    color: FUEL_COLORS.lpg.primary,
  },
} satisfies ChartConfig;

// 유가 이력 그래프 컴포넌트
interface PriceHistoryChartProps {
  priceHistory: PriceHistoryItem[];
  station: PetroleumStation;
}

function PriceHistoryChart({ priceHistory, station }: PriceHistoryChartProps) {
  // 차트 데이터 변환 (같은 날의 변동도 표시, 날짜별 라벨 그룹화)
  let lastDateLabel = '';

  const chartData = priceHistory
    .slice()
    .sort((a, b) => {
      const dateA = a.recordedAt ? new Date(a.recordedAt).getTime() : 0;
      const dateB = b.recordedAt ? new Date(b.recordedAt).getTime() : 0;
      return dateA - dateB;
    })
    .map((item, index) => {
      const date = item.recordedAt ? new Date(item.recordedAt) : null;

      // 날짜 라벨 (M/D 형식)
      const dateOnly = date
        ? `${date.getMonth() + 1}/${date.getDate()}`
        : '';

      // 날짜가 바뀔 때만 라벨 표시, 같은 날은 빈 문자열
      const showLabel = dateOnly !== lastDateLabel;
      if (showLabel) {
        lastDateLabel = dateOnly;
      }

      // 툴팁용 전체 날짜/시간
      const fullDateTime = date
        ? date.toLocaleString('ko-KR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
        : '';

      return {
        id: index,
        date: showLabel ? dateOnly : '',
        dateOnly, // 실제 날짜 (정렬용)
        fullDateTime,
        gas: item.gas || null,
        diesel: item.diesel || null,
        premiumGas: item.premiumGas || null,
        kerosene: item.kerosene || null,
        lpg: item.lpg || null,
      };
    });

  // 표시할 유종 결정 (현재 가격이 있는 유종만)
  const hasGas = (station.priceGasoline ?? 0) > 0;
  const hasDiesel = (station.priceDiesel ?? 0) > 0;
  const hasPremiumGas = (station.pricePremiumGas ?? 0) > 0;
  const hasKerosene = (station.priceKerosene ?? 0) > 0;
  const hasLpg = (station.priceLpg ?? 0) > 0;

  if (chartData.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 text-sm">
        유가 이력 데이터가 없습니다.
      </div>
    );
  }

  return (
    <ChartContainer
      config={priceChartConfig}
      className="h-[200px] w-full bg-slate-800 rounded-lg p-2"
    >
      <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#475569" />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          fontSize={11}
          interval={0}
          height={30}
          tick={{ fill: '#e2e8f0' }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          fontSize={10}
          tickFormatter={(value) => `${value.toLocaleString()}`}
          domain={['auto', 'auto']}
          tick={{ fill: '#e2e8f0' }}
        />
        <ChartTooltip
          content={({ active, payload, label }) => {
            if (!active || !payload || payload.length === 0) {
              return null;
            }

            // payload를 가격순으로 정렬 (높은 가격부터)
            const sortedPayload = [...payload].sort((a, b) => {
              const valueA = typeof a.value === 'number' ? a.value : 0;
              const valueB = typeof b.value === 'number' ? b.value : 0;
              return valueB - valueA; // 내림차순 정렬
            });

            const fullDateTime = payload[0]?.payload?.fullDateTime || label;

            return (
              <div className="grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
                <div className="font-medium">{fullDateTime}</div>
                <div className="grid gap-1.5">
                  {sortedPayload.map((item) => {
                    const fuelKey = item.dataKey as keyof typeof FUEL_COLORS;
                    const fuelColor = FUEL_COLORS[fuelKey]?.primary;
                    const label = priceChartConfig[fuelKey]?.label || item.dataKey || '';

                    return (
                      <div
                        key={item.dataKey}
                        className="flex w-full items-center justify-between gap-2"
                      >
                        <div className="flex items-center gap-2">
                          {fuelColor && (
                            <div
                              className="w-2.5 h-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: fuelColor }}
                            />
                          )}
                          <span className="text-muted-foreground">{label}</span>
                        </div>
                        <span className="font-mono font-medium tabular-nums text-foreground">
                          {typeof item.value === 'number' ? `${item.value.toLocaleString()}원` : item.value}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }}
        />
        {hasGas && (
          <Line
            type="monotone"
            dataKey="gas"
            stroke={FUEL_COLORS.gas.primary}
            strokeWidth={2}
            dot={{ r: 3, fill: FUEL_COLORS.gas.primary, strokeWidth: 0 }}
            activeDot={{ r: 5, fill: FUEL_COLORS.gas.primary, strokeWidth: 2, stroke: '#fff' }}
            connectNulls
          />
        )}
        {hasDiesel && (
          <Line
            type="monotone"
            dataKey="diesel"
            stroke={FUEL_COLORS.diesel.primary}
            strokeWidth={2}
            dot={{ r: 3, fill: FUEL_COLORS.diesel.primary, strokeWidth: 0 }}
            activeDot={{ r: 5, fill: FUEL_COLORS.diesel.primary, strokeWidth: 2, stroke: '#fff' }}
            connectNulls
          />
        )}
        {hasPremiumGas && (
          <Line
            type="monotone"
            dataKey="premiumGas"
            stroke={FUEL_COLORS.premiumGas.primary}
            strokeWidth={2}
            dot={{ r: 3, fill: FUEL_COLORS.premiumGas.primary, strokeWidth: 0 }}
            activeDot={{ r: 5, fill: FUEL_COLORS.premiumGas.primary, strokeWidth: 2, stroke: '#fff' }}
            connectNulls
          />
        )}
        {hasKerosene && (
          <Line
            type="monotone"
            dataKey="kerosene"
            stroke={FUEL_COLORS.kerosene.primary}
            strokeWidth={2}
            dot={{ r: 3, fill: FUEL_COLORS.kerosene.primary, strokeWidth: 0 }}
            activeDot={{ r: 5, fill: FUEL_COLORS.kerosene.primary, strokeWidth: 2, stroke: '#fff' }}
            connectNulls
          />
        )}
        {hasLpg && (
          <Line
            type="monotone"
            dataKey="lpg"
            stroke={FUEL_COLORS.lpg.primary}
            strokeWidth={2}
            dot={{ r: 3, fill: FUEL_COLORS.lpg.primary, strokeWidth: 0 }}
            activeDot={{ r: 5, fill: FUEL_COLORS.lpg.primary, strokeWidth: 2, stroke: '#fff' }}
            connectNulls
          />
        )}
      </LineChart>
    </ChartContainer>
  );
}

// 선택된 주유소 상세 정보 플로팅 카드
interface StationDetailCardProps {
  station: PetroleumStation;
  onClose: () => void;
}

function StationDetailCard({ station, onClose }: StationDetailCardProps) {
  const [detailStation, setDetailStation] = useState<PetroleumStation | null>(null);
  const [loading, setLoading] = useState(false);

  // 상세 정보 API 호출 (7일 고정)
  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const detail = await FuelStationApiService.getStationDetail(station.id, 7);
        setDetailStation(detail);
      } catch (error) {
        console.error('Failed to fetch station detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [station.id]);

  // 표시할 주유소 정보 (상세 정보가 있으면 사용, 없으면 기본 정보 사용)
  const displayStation = detailStation || station;
  // 모바일: 헤더를 아래로 쓸면 닫기
  const swipe = useSwipeY(d => { if (d === 'down') onClose(); });

  return (
    <div className="fixed bottom-4 left-4 md:bottom-auto md:top-4 md:left-[25rem] z-[1002] w-[calc(100%-2rem)] md:w-[420px] max-h-[80vh] md:max-h-[calc(100vh-2rem)] bg-white rounded-lg shadow-2xl border border-gray-200 animate-in slide-in-from-bottom md:slide-in-from-left duration-300 overflow-hidden">
      <div className="max-h-[80vh] md:max-h-[calc(100vh-2rem)] overflow-y-auto overscroll-contain">
        {/* 헤더 - 썸네일 이미지 포함 (모바일: 아래로 쓸면 닫기) */}
        <div className="sticky top-0 z-10 touch-pan-x" {...swipe}>
          {/* 썸네일 이미지 영역 */}
          {displayStation.thumbnailUrl ? (
            <div className="relative h-40 w-full">
              <img
                src={displayStation.thumbnailUrl}
                alt={`${displayStation.name} 이미지`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // 이미지 로드 실패 시 기본 배경으로 대체
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute top-2 right-2 flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (displayStation.thumbnailUrl) {
                      window.open(displayStation.thumbnailUrl, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  className="h-8 w-8 p-0 hover:bg-white/20 text-white shrink-0 bg-black/30 rounded-full"
                  title="이미지 크게 보기"
                >
                  <Expand className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="h-8 w-8 p-0 hover:bg-white/20 text-white shrink-0 bg-black/30 rounded-full"
                >
                  <X className="size-4" />
                </Button>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="size-5 text-white" />
                  <h3 className="font-bold text-lg text-white">{displayStation.name}</h3>
                </div>
                <p className="text-sm text-white/90">{displayStation.address}</p>
                {/* 평점 표시 */}
                {displayStation.rating && displayStation.rating > 0 && (
                  <div className="flex items-center gap-1 mt-2">
                    <Star className="size-4 text-yellow-400 fill-yellow-400" />
                    <span className="text-white font-medium">{displayStation.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-4 flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="size-5 text-white" />
                  <h3 className="font-bold text-lg text-white">{displayStation.name}</h3>
                </div>
                <p className="text-sm text-blue-50">{displayStation.address}</p>
                {/* 평점 표시 */}
                {displayStation.rating && displayStation.rating > 0 && (
                  <div className="flex items-center gap-1 mt-2">
                    <Star className="size-4 text-yellow-300 fill-yellow-300" />
                    <span className="text-white font-medium">{displayStation.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0 hover:bg-blue-700 text-white shrink-0"
              >
                <X className="size-4" />
              </Button>
            </div>
          )}
        </div>

        {/* 컨텐츠 */}
        <div className="p-4 space-y-4">
          {/* 좌표 미보유 안내 */}
          {!hasLocation(displayStation) && (
            <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <MapPinOff className="size-4 shrink-0" />
              <span>좌표 정보가 없어 지도에 표시되지 않습니다.</span>
            </div>
          )}

          {/* 영업 상태 및 주유소 타입 */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={displayStation.isOpen ? "default" : "secondary"} className="text-sm px-3 py-1">
              {displayStation.isOpen ? "영업중" : "휴업"}
            </Badge>
            {displayStation.is24Hours && (
              <Badge variant="outline" className="text-sm px-3 py-1 border-orange-500 text-orange-600">
                24시간
              </Badge>
            )}
            {detailStation?.isSelf && (
              <Badge variant="outline" className="text-sm px-3 py-1 border-green-500 text-green-600">
                셀프
              </Badge>
            )}
            {detailStation?.isLpg && (
              <Badge variant="outline" className="text-sm px-3 py-1 border-teal-500 text-teal-600">
                LPG
              </Badge>
            )}
          </div>

          {/* 시설정보 */}
          {(displayStation.hasCarWash || displayStation.hasConvenienceStore || displayStation.hasMaintenance || displayStation.hasRestroom) && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">시설 정보</h4>
              <div className="flex flex-wrap gap-2">
                {displayStation.hasCarWash && (
                  <div className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm">
                    <Car className="size-4" />
                    <span>세차</span>
                  </div>
                )}
                {displayStation.hasConvenienceStore && (
                  <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1.5 rounded-full text-sm">
                    <Store className="size-4" />
                    <span>편의점</span>
                  </div>
                )}
                {displayStation.hasMaintenance && (
                  <div className="flex items-center gap-1.5 bg-orange-50 text-orange-700 px-3 py-1.5 rounded-full text-sm">
                    <Wrench className="size-4" />
                    <span>정비</span>
                  </div>
                )}
                {displayStation.hasRestroom && (
                  <div className="flex items-center gap-1.5 bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full text-sm">
                    <Bath className="size-4" />
                    <span>화장실</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 가격 정보 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1">
              <Fuel className="size-4" />
              유류 가격
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-200">
                  {(displayStation.pricePremiumGas ?? 0) > 0 && (
                    <tr>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FUEL_COLORS.premiumGas.primary }} />
                          <span className="font-medium text-gray-700">고급휘발유</span>
                        </div>
                      </td>
                      <td className="py-2 text-right">
                        <span className="font-bold" style={{ color: FUEL_COLORS.premiumGas.text }}>
                          {displayStation.pricePremiumGas!.toLocaleString()}원
                        </span>
                      </td>
                    </tr>
                  )}
                  {(displayStation.priceGasoline ?? 0) > 0 ? (
                    <tr>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FUEL_COLORS.gas.primary }} />
                          <span className="font-medium text-gray-700">휘발유</span>
                        </div>
                      </td>
                      <td className="py-2 text-right">
                        <span className="font-bold" style={{ color: FUEL_COLORS.gas.text }}>
                          {displayStation.priceGasoline!.toLocaleString()}원
                        </span>
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-gray-300" />
                          <span className="font-medium text-gray-500">휘발유</span>
                        </div>
                      </td>
                      <td className="py-2 text-right">
                        <span className="text-gray-400">정보 없음</span>
                      </td>
                    </tr>
                  )}
                  {(displayStation.priceDiesel ?? 0) > 0 ? (
                    <tr>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FUEL_COLORS.diesel.primary }} />
                          <span className="font-medium text-gray-700">경유</span>
                        </div>
                      </td>
                      <td className="py-2 text-right">
                        <span className="font-bold" style={{ color: FUEL_COLORS.diesel.text }}>
                          {displayStation.priceDiesel!.toLocaleString()}원
                        </span>
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-gray-300" />
                          <span className="font-medium text-gray-500">경유</span>
                        </div>
                      </td>
                      <td className="py-2 text-right">
                        <span className="text-gray-400">정보 없음</span>
                      </td>
                    </tr>
                  )}
                  {(displayStation.priceKerosene ?? 0) > 0 && (
                    <tr>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FUEL_COLORS.kerosene.primary }} />
                          <span className="font-medium text-gray-700">등유</span>
                        </div>
                      </td>
                      <td className="py-2 text-right">
                        <span className="font-bold" style={{ color: FUEL_COLORS.kerosene.text }}>
                          {displayStation.priceKerosene!.toLocaleString()}원
                        </span>
                      </td>
                    </tr>
                  )}
                  {(displayStation.priceLpg ?? 0) > 0 && (
                    <tr>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FUEL_COLORS.lpg.primary }} />
                          <span className="font-medium text-gray-700">LPG</span>
                        </div>
                      </td>
                      <td className="py-2 text-right">
                        <span className="font-bold" style={{ color: FUEL_COLORS.lpg.text }}>
                          {displayStation.priceLpg!.toLocaleString()}원
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {displayStation.priceUpdatedAt && (() => {
              const dateStr = displayStation.priceUpdatedAt;
              if (!dateStr || typeof dateStr !== 'string') return null;

              try {
                const date = new Date(dateStr);
                if (!isNaN(date.getTime()) && date.getFullYear() > 2000) {
                  return (
                    <div className="text-xs text-gray-500 mt-2">
                      업데이트: {date.toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  );
                }
              } catch {
                return null;
              }
              return null;
            })()}
          </div>

          {/* 유가 이력 그래프 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1">
              <TrendingUp className="size-4" />
              유가 이력 (최근 7일)
            </h4>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="size-6 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">유가 이력을 불러오는 중...</span>
              </div>
            ) : detailStation?.priceHistory && detailStation.priceHistory.length > 0 ? (
              <PriceHistoryChart priceHistory={detailStation.priceHistory} station={displayStation} />
            ) : (
              <div className="text-center py-4 text-gray-500 text-sm bg-gray-50 rounded-lg">
                유가 이력 데이터가 없습니다.
              </div>
            )}
          </div>

          {/* 운영 정보 */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Clock className="size-4 text-gray-500" />
              <span>
                {displayStation.is24Hours
                  ? '24시간 영업'
                  : displayStation.businessHours || displayStation.operatingHours}
              </span>
            </div>
            {displayStation.phone && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Phone className="size-4 text-gray-500" />
                <span>{displayStation.phone}</span>
              </div>
            )}
            {displayStation.bizno && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Building2 className="size-4 text-gray-500" />
                <span className="text-gray-500">사업자번호</span>
                <span>{displayStation.bizno}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Store className="size-4 text-gray-500" />
              <span className="text-gray-500">Opinet ID</span>
              <span>{displayStation.id}</span>
            </div>
            {displayStation.storeCd && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Store className="size-4 text-gray-500" />
                <span className="text-gray-500">Store Code</span>
                <span>{displayStation.storeCd}</span>
              </div>
            )}
          </div>

          {/* 편의시설 */}
          {displayStation.amenities.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">편의시설</h4>
              <div className="flex flex-wrap gap-1">
                {displayStation.amenities.map((amenity) => (
                  <Badge key={amenity} variant="outline" className="text-xs">
                    {amenity}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// 메인 주유소 지도 뷰 컴포넌트
export function StationMapView() {
  const [selectedStation, setSelectedStation] = useState<PetroleumStation | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showSidePanel, setShowSidePanel] = useState(true);
  const [sheetExpanded, setSheetExpanded] = useState(false); // 모바일 시트 전체화면
  const [mapCenter, setMapCenter] = useState<[number, number]>([37.5665, 126.9780]); // 서울 중심
  const [stations, setStations] = useState<PetroleumStation[]>([]);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef<LeafletMap | null>(null);

  // 필터 상태
  const [filterBrands, setFilterBrands] = useState<string[]>([]);
  const [filter24h, setFilter24h] = useState(false);
  const [filterCarWash, setFilterCarWash] = useState(false);
  const [filterConvenienceStore, setFilterConvenienceStore] = useState(false);
  const [filterMaintenance, setFilterMaintenance] = useState(false);
  const [filterSelf, setFilterSelf] = useState(false);
  const [filterLpg, setFilterLpg] = useState(false);
  // 우측 버튼 열의 펼침 패널 — 모바일에서 겹치지 않도록 한 번에 하나만
  const [openPanel, setOpenPanel] = useState<'filter' | 'price' | 'stats' | 'sales' | null>(null);

  // 가격 시각화 상태
  const [priceFuel, setPriceFuel] = useState<FuelType>('gas');
  const [colorByPrice, setColorByPrice] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showOutliers, setShowOutliers] = useState(true);
  const [timelineOn, setTimelineOn] = useState(false);
  const [timelineIdx, setTimelineIdx] = useState(TIMELINE_DAYS - 1);
  const [histories, setHistories] = useState<Map<string, DailyPriceResponse[]>>(new Map());
  const [regionPrices, setRegionPrices] = useState<RegionPriceResponse[] | null>(null);
  const [mapZoom, setMapZoom] = useState(12);

  // 내 주유소 — 접근 가능한 주유소 목록(GET /me/stores)에서 선택
  const [myStores, setMyStores] = useState<MyStore[]>([]);
  const [myStoreCd, setMyStoreCd] = useState('');
  useEffect(() => {
    apiClient
      .get('me/stores')
      .json<{ result?: MyStore[] }>()
      .then(res => setMyStores(res.result ?? []))
      .catch(err => console.error('내 주유소 목록 조회 실패:', err));
  }, []);
  // 미선택이거나 목록에 없으면 첫 주유소를 사용 (단일 소속이면 그대로 자동 선택된다)
  const activeStore = myStores.find(s => s.storeCd === myStoreCd) ?? myStores[0];
  const timelineDates = useMemo(() => recentDates(TIMELINE_DAYS), []);

  // 지역 선택 상태
  const [showRegionOverlay, setShowRegionOverlay] = useState(false);
  const [selectedSidoCode, setSelectedSidoCode] = useState<string | null>(null);
  const [selectedSidoName, setSelectedSidoName] = useState<string | null>(null);
  const [selectedSigunguName, setSelectedSigunguName] = useState<string | null>(null);

  // SGIS 통계 오버레이 상태
  const [showStats, setShowStats] = useState(false);
  const [statsMetric, setStatsMetric] = useState<MetricKey>('ppltnDnsty');
  const [statsByCode, setStatsByCode] = useState<Map<string, CensusSummary>>(new Map());
  const [statsLoading, setStatsLoading] = useState(false);

  // 전국 스냅샷 — IndexedDB 저장본으로 즉시 렌더하고 백그라운드 조건부 GET(ETag)으로 갱신
  const [allStations, setAllStations] = useState<LocatedStation[]>([]);
  useEffect(() => {
    setLoading(true);
    return loadStationSnapshot(
      snap => { setAllStations(snap.stations); setLoading(false); },
      err => { console.error('전국 주유소 스냅샷 로드 실패:', err); setLoading(false); }
    );
  }, []);

  // 매출 비례 심볼 — 접근 가능한 매장의 월 매출을 원 넓이로 그린다
  const [showSales, setShowSales] = useState(false);
  const [salesMonth, setSalesMonth] = useState(currentYearMonth);
  const [salesByStore, setSalesByStore] = useState<Map<string, number>>(new Map());
  const [salesLoading, setSalesLoading] = useState(false);
  useEffect(() => {
    if (!showSales) return;
    let cancelled = false;
    setSalesLoading(true);
    apiClient
      .get('me/stores/sales', { searchParams: { yearMonth: salesMonth } })
      .json<{ result?: { storeCd: string; totalAmount: number }[] }>()
      .then(res => {
        if (cancelled) return;
        setSalesByStore(new Map((res.result ?? []).map(r => [r.storeCd, r.totalAmount])));
      })
      .catch(err => { if (!cancelled) { console.error('매장 매출 조회 실패:', err); setSalesByStore(new Map()); } })
      .finally(() => { if (!cancelled) setSalesLoading(false); });
    return () => { cancelled = true; };
  }, [showSales, salesMonth]);

  // 매장 → 좌표는 이미 받아둔 전국 스냅샷에서 오피넷 ID 로 찾는다 (매출 API 는 금액만 내려준다)
  const salesPoints = useMemo<StoreSalesPoint[]>(() => {
    if (salesByStore.size === 0 || allStations.length === 0) return [];
    const byId = new Map(allStations.map(s => [s.id, s]));
    return myStores.flatMap(store => {
      const amount = salesByStore.get(store.storeCd);
      const station = store.opinetId ? byId.get(store.opinetId) : undefined;
      if (!amount || !station) return [];
      return [{
        storeName: store.storeName,
        latitude: station.latitude,
        longitude: station.longitude,
        amount,
      }];
    });
  }, [myStores, salesByStore, allStations]);

  // 지역 선택 모드이거나 텍스트 검색 중이면 뷰포트 계산을 멈춘다 (그 모드가 stations 를 채운다)
  const viewportPaused = showRegionOverlay || debouncedSearchQuery.trim().length >= 2; // 검색 effect 와 같은 기준

  // 타임라인이 켜져 있으면 기준일의 이력 가격으로 바꿔 끼운 목록 (오늘은 현재 가격 그대로)
  const timelineDate = timelineDates[timelineIdx];
  const live = !timelineOn || timelineIdx === TIMELINE_DAYS - 1;
  const displayStations = useMemo(
    () => (live ? stations : applyDailyPrices(stations, histories, timelineDate)),
    [live, stations, histories, timelineDate]
  );

  // 마커 원본 — 일반 모드는 전국 스냅샷을 통째로 물린다. 팬(moveend)마다 뷰포트 배열을
  // 새로 만들면 markercluster 가 매번 처음부터 재클러스터링해 클러스터가 계속 바뀐다.
  // 화면 밖 마커는 markercluster 가 자체 관리하므로 전체를 넣고 건드리지 않는 게 맞다.
  // 검색·지역 모드는 그 결과만, 타임라인 모드는 이력 로드가 뷰포트 300곳 기준이라 뷰포트 목록을 쓴다.
  const markerSource = viewportPaused || timelineOn ? displayStations : allStations;

  // 필터 적용된 마커 목록 (지역·텍스트 검색 결과는 클라이언트에서 거른다)
  const filteredStations = useMemo(() => {
    return markerSource.filter(s => {
      if (filterBrands.length > 0 && (!s.pollCode || !filterBrands.includes(s.pollCode))) return false;
      if (filter24h && !s.is24Hours) return false;
      if (filterCarWash && !s.hasCarWash) return false;
      if (filterConvenienceStore && !s.hasConvenienceStore) return false;
      if (filterMaintenance && !s.hasMaintenance) return false;
      if (filterSelf && !s.isSelf) return false;
      if (filterLpg && !s.isLpg) return false;
      return true;
    });
  }, [markerSource, filterBrands, filter24h, filterCarWash, filterConvenienceStore, filterMaintenance, filterSelf, filterLpg]);

  // 가격 분위 스케일(마커 색)·이상치·히트맵
  const priceScale = useMemo(
    () => (colorByPrice ? buildChoroplethScale(filteredStations.map(s => getPrice(s, priceFuel) ?? NaN), PRICE_RAMP) : null),
    [colorByPrice, filteredStations, priceFuel]
  );
  const outliers = useMemo(
    () => (showOutliers ? findOutliers(filteredStations, priceFuel) : new Map<string, number>()),
    [showOutliers, filteredStations, priceFuel]
  );
  const heatLevel: HeatLevel = mapZoom < 10 ? 'sido' : 'sigungu';
  const heatValues = useMemo(
    () => (showHeatmap && regionPrices ? regionAverages(regionPrices, priceFuel, heatLevel) : new Map<string, number>()),
    [showHeatmap, regionPrices, priceFuel, heatLevel]
  );
  const heatScale = useMemo(
    () => (heatValues.size ? buildChoroplethScale(Array.from(heatValues.values()), PRICE_RAMP) : null),
    [heatValues]
  );

  // 히트맵을 켜면 지역 집계를 1회 로드
  useEffect(() => {
    if (!showHeatmap || regionPrices) return;
    FuelStationApiService.getRegionPrices()
      .then(setRegionPrices)
      .catch(err => console.error('지역 유가 집계 조회 실패:', err));
  }, [showHeatmap, regionPrices]);

  // 타임라인이 켜져 있으면 현재 목록 주유소들의 30일 이력 로드
  // 이력 API 는 @Size(max=300) — 가까운 순 300곳만 (stations 는 뷰포트 모드에서 거리순)
  const stationIdsKey = useMemo(() => stations.slice(0, 300).map(s => s.id).sort().join(','), [stations]);
  useEffect(() => {
    if (!timelineOn || !stationIdsKey) return;
    let cancelled = false;
    FuelStationApiService.getDailyPrices(stationIdsKey.split(','), TIMELINE_DAYS)
      .then(rows => { if (!cancelled) setHistories(groupDailyPrices(rows)); })
      .catch(err => console.error('유가 이력 조회 실패:', err));
    return () => { cancelled = true; };
  }, [timelineOn, stationIdsKey]);

  // 뷰포트 갱신 — 스냅샷에서 현재 경계 안 주유소를 중심 가까운 순으로 뽑는다 (사이드 목록·타임라인용)
  const viewportPausedRef = useRef(viewportPaused);
  viewportPausedRef.current = viewportPaused;
  const allStationsRef = useRef(allStations);
  allStationsRef.current = allStations;

  const loadViewport = useCallback(() => {
    const map = mapRef.current;
    if (!map || viewportPausedRef.current) return;
    const bounds = map.getBounds();
    const center = map.getCenter();
    const inView = allStationsRef.current
      .filter(s => bounds.contains([s.latitude, s.longitude]))
      .map(s => ({ ...s, distance: center.distanceTo([s.latitude, s.longitude]) / 1000 }))
      .sort((a, b) => a.distance - b.distance);
    setStations(inView);
  }, []);

  // 네트워크 왕복이 없어 디바운스 불필요 (전국 1.1만 건 필터 ~수 ms)
  const handleViewportChange = loadViewport;

  // 스냅샷이 도착·갱신되거나 지역/검색 모드에서 빠져나오면 현재 뷰포트를 다시 계산
  useEffect(() => {
    loadViewport();
  }, [allStations, viewportPaused, loadViewport]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search stations when query changes
  useEffect(() => {
    const query = debouncedSearchQuery.trim();
    if (query.length < 2) return;

    let cancelled = false;
    const searchStations = async () => {
      try {
        setLoading(true);
        // 현재 지도 범위 근처(5~50km)를 먼저 찾고, 없으면 전국에서 찾는다
        const map = mapRef.current;
        let result: PetroleumStation[] = [];
        if (map) {
          const bounds = map.getBounds();
          const center = bounds.getCenter();
          const radiusKm = Math.min(50, Math.max(5, center.distanceTo(bounds.getNorthEast()) / 1000));
          result = await FuelStationApiService.searchCombined(query, center.lat, center.lng, radiusKm);
        }
        if (result.length === 0) {
          result = await FuelStationApiService.searchByText(query);
        }
        if (!cancelled) setStations(result);
      } catch (error) {
        console.error('Failed to search stations:', error);
        if (!cancelled) setStations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    searchStations();
    return () => { cancelled = true; };
  }, [debouncedSearchQuery]);

  // 주유소 선택 핸들러
  const handleStationSelect = useCallback((station: PetroleumStation) => {
    setSelectedStation(station);
    // 좌표가 없는 주유소는 이동할 위치가 없으므로 지도를 그대로 둔다
    if (!hasLocation(station)) return;
    setMapCenter([station.latitude, station.longitude]);
    // 줌 레벨 변경 없이 해당 위치로 이동
    if (mapRef.current) {
      mapRef.current.panTo([station.latitude, station.longitude]);
    }
  }, []);

  // 내 주유소가 정해지면 그 위치로 지도를 이동하고 선택 상태로 둔다
  // (오피넷 ID 미등록이면 이동할 위치가 없으므로 지도 기본 위치를 유지한다)
  const activeOpinetId = activeStore?.opinetId ?? null;
  useEffect(() => {
    if (!activeOpinetId) return;
    let cancelled = false;
    FuelStationApiService.getStationDetail(activeOpinetId).then(station => {
      if (cancelled || !station || !hasLocation(station)) return;
      setSelectedStation(station);
      setMapCenter([station.latitude, station.longitude]);
      mapRef.current?.setView([station.latitude, station.longitude], 15);
    });
    return () => { cancelled = true; };
  }, [activeOpinetId]);

  // 지역 선택 핸들러
  const handleSidoSelect = useCallback((code: string, name: string, _bounds: L.LatLngBounds) => {
    setSelectedSidoCode(code);
    setSelectedSidoName(name);
    setSelectedSigunguName(null);
  }, []);

  const handleSigunguSelect = useCallback(async (region: RegionInfo) => {
    setSelectedSigunguName(region.name);
    // 시도 미선택 상태에서는 region API 호출 불가(빈 sido 방어)
    if (!selectedSidoName) {
      return;
    }
    const sigungu = geoSigunguToOpinet(region.name);
    try {
      setLoading(true);
      let result = await FuelStationApiService.searchByRegion(selectedSidoName, sigungu);
      // 일반구·창원 통합구는 opinet 이 시 단위로만 저장 → 클릭한 구 폴리곤으로 좌표 필터
      if (needsGuPolygonFilter(region.name) && region.geometry) {
        // 좌표가 없으면 구 판별이 불가하므로 목록에는 남긴다
        result = result.filter(s => !hasLocation(s) || isPointInGeometry(s.latitude, s.longitude, region.geometry!));
      }
      setStations(result);
    } catch (err) {
      console.error('지역 주유소 검색 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedSidoName]);

  const clearRegionOverlay = useCallback(() => {
    setShowRegionOverlay(false);
    setSelectedSidoCode(null);
    setSelectedSidoName(null);
    setSelectedSigunguName(null);
    setShowStats(false);
    setStatsByCode(new Map());
  }, []);

  const goBackToSido = useCallback(() => {
    setSelectedSidoCode(null);
    setSelectedSidoName(null);
    setSelectedSigunguName(null);
  }, []);

  // 시군구 → 시도 레벨로 돌아가기 (시도는 유지)
  const goBackFromSigungu = useCallback(() => {
    setSelectedSigunguName(null);
  }, []);

  // 통계 오버레이 토글 — 켜면 지역 오버레이도 함께 활성화
  const toggleStats = useCallback(() => {
    setShowStats(prev => {
      const next = !prev;
      if (next) {
        setShowRegionOverlay(true);
        setShowHeatmap(false);
      }
      setOpenPanel(next ? 'stats' : null);
      return next;
    });
  }, []);

  // 현재 레벨(시도/시군구)의 통계를 조회해 admCd → 통계 Map 으로 보관
  useEffect(() => {
    if (!showStats || !showRegionOverlay) return;

    let cancelled = false;
    const load = async () => {
      try {
        setStatsLoading(true);
        const summaries = selectedSidoCode
          ? await fetchSigunguSummaries(STATS_YEAR)
          : await fetchSidoSummaries(STATS_YEAR);
        if (!cancelled) setStatsByCode(indexByAdmCd(summaries));
      } catch (err) {
        console.error('SGIS 통계 조회 실패:', err);
        if (!cancelled) setStatsByCode(new Map());
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [showStats, showRegionOverlay, selectedSidoCode]);

  // 선택 지표의 값 분포로 색상 스케일 산출 (null 안전)
  const statsScale = useMemo(() => {
    if (!showStats) return null;
    const values: number[] = [];
    statsByCode.forEach(summary => {
      const v = getMetricValue(summary, statsMetric);
      if (v !== undefined) values.push(v);
    });
    return buildChoroplethScale(values);
  }, [showStats, statsByCode, statsMetric]);

  // GeoJSON 지역 → fillColor (통계 OFF 또는 스케일 없음 시 undefined → 기본 스타일)
  const colorByCode = useCallback(
    (admCd: string): string | undefined => {
      if (!showStats || !statsScale) return undefined;
      return statsScale.colorFor(getMetricValue(statsByCode.get(admCd), statsMetric));
    },
    [showStats, statsScale, statsByCode, statsMetric]
  );

  // 사이드패널 토글
  const toggleSidePanel = useCallback(() => {
    setShowSidePanel(!showSidePanel);
    setSheetExpanded(false);
  }, [showSidePanel]);

  // 모바일 시트 쓸어넘김: 위 → 전체화면, 아래 → 전체화면이면 반으로, 반이면 닫기
  const handleSheetSwipe = useCallback((direction: 'up' | 'down') => {
    if (direction === 'up') {
      setSheetExpanded(true);
    } else if (sheetExpanded) {
      setSheetExpanded(false);
    } else {
      setShowSidePanel(false);
    }
  }, [sheetExpanded]);

  // 지도 인스턴스 참조 설정은 ref로 처리

  return (
    <div className="h-full relative">
      {/* 지도 영역 - 전체 화면 */}
      <div className="absolute inset-0">
        {!showSidePanel && (
          <Button
            className="absolute z-[1000] bottom-6 left-1/2 -translate-x-1/2 rounded-full shadow-lg md:bottom-auto md:top-4 md:left-4 md:translate-x-0 md:rounded-md"
            onClick={toggleSidePanel}
          >
            <Search className="size-4 mr-1" />
            주유소 검색
          </Button>
        )}

        {/* 지역 선택 브레드크럼 */}
        {showRegionOverlay && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-1 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-md text-sm border border-blue-200">
            <button
              className={`font-medium transition-colors ${selectedSidoName ? 'text-gray-500 hover:text-gray-800' : 'text-blue-600'}`}
              onClick={selectedSidoName ? goBackToSido : undefined}
            >
              전국
            </button>
            {selectedSidoName && (
              <>
                <ChevronRight className="size-3 text-gray-400 shrink-0" />
                <button
                  className={`font-medium transition-colors ${selectedSigunguName ? 'text-gray-500 hover:text-gray-800' : 'text-blue-600'}`}
                  onClick={selectedSigunguName ? goBackFromSigungu : undefined}
                >
                  {selectedSidoName}
                </button>
              </>
            )}
            {selectedSigunguName && (
              <>
                <ChevronRight className="size-3 text-gray-400 shrink-0" />
                <span className="text-blue-600 font-semibold">{selectedSigunguName}</span>
              </>
            )}
            <button onClick={clearRegionOverlay} className="ml-1 text-gray-400 hover:text-gray-600" title="지역 선택 닫기">
              <X className="size-3.5" />
            </button>
          </div>
        )}

        <MapContainer
          center={mapCenter}
          zoom={12}
          scrollWheelZoom={true}
          className="h-full w-full"
          ref={mapRef}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <ScaleControl position="bottomright" />

          {/* 지도 이벤트 핸들러 */}
          <MapEventHandler onViewportChange={handleViewportChange} onZoom={setMapZoom} />

          {/* 지역 평균 유가 히트맵 (줌 10 미만 시도, 이상 시군구) */}
          <PriceHeatLayer active={showHeatmap} level={heatLevel} values={heatValues} scale={heatScale} />

          {/* 주유소 마커들 (클러스터링) */}
          <MarkerClusterGroupLayer
            stations={filteredStations}
            selectedStation={selectedStation}
            onSelect={handleStationSelect}
            priceFuel={priceFuel}
            priceScale={priceScale}
          />

          {/* 매장 매출 비례 심볼 (마커 클러스터와 별도 레이어) */}
          <StoreSalesLayer active={showSales} points={salesPoints} />

          {/* 한국 행정구역 지역 선택 레이어 */}
          <KoreaRegionLayer
            active={showRegionOverlay}
            selectedSidoCode={selectedSidoCode}
            onSidoSelect={handleSidoSelect}
            onSigunguSelect={handleSigunguSelect}
            colorByCode={colorByCode}
          />
        </MapContainer>

        {/* 최초 스냅샷 로딩 — 캐시가 없으면 지도에 보여줄 게 없으므로 화면 중앙에 표시 */}
        {loading && allStations.length === 0 && (
          <div className="absolute inset-0 z-[1000] flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-3 shadow-lg border border-gray-200">
              <Loader2 className="size-5 animate-spin text-blue-600" />
              <span className="text-sm text-gray-700">전국 주유소 정보를 불러오는 중...</span>
            </div>
          </div>
        )}

        {/* 하단 도크: 타임라인. 모바일에서 하단 시트가 열려 있으면 그 위로 올린다 */}
        <div
          className={`absolute left-1/2 -translate-x-1/2 z-[1000] flex-col items-center gap-2 w-[min(520px,calc(100%-1.5rem))] md:flex md:bottom-8 ${
            !showSidePanel ? 'flex bottom-12' : sheetExpanded ? 'hidden' : 'flex bottom-[calc(50vh+0.75rem)]'
          }`}
        >
          {timelineOn && (
            <div className="w-full bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2 shadow-md border border-gray-200 text-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-gray-700">{timelineDate} 기준 {FUEL_LABELS[priceFuel]}</span>
                <span className="text-gray-500">
                  {live ? '현재 가격' : `이력 보유 ${histories.size}곳 / ${stations.length}곳`}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={TIMELINE_DAYS - 1}
                value={timelineIdx}
                onChange={e => setTimelineIdx(Number(e.target.value))}
                className="w-full"
                aria-label="가격 기준일"
              />
            </div>
          )}
        </div>

        {/* SGIS 통계 단계구분도 범례 */}
        {showStats && (
          <StatisticsLegend
            scale={statsScale}
            metric={getMetricDef(statsMetric)}
            loading={statsLoading}
          />
        )}

        {/* 가격 히트맵 범례 */}
        {showHeatmap && (
          <StatisticsLegend
            scale={heatScale}
            metric={{
              label: `${FUEL_LABELS[priceFuel]} 평균가 (${heatLevel === 'sido' ? '시도' : '시군구'})`,
              unit: '원',
              format: v => Math.round(v).toLocaleString(),
            }}
            loading={!regionPrices}
          />
        )}

        {/* 매출 비례 심볼 범례 */}
        {showSales && (
          <SalesLegend month={salesMonth} count={salesPoints.length} loading={salesLoading} />
        )}

        {/* 우측 툴바: 줌 + 오버레이 토글. 열린 패널은 툴바 왼쪽 한 자리에만 그린다 */}
        <div className="absolute top-4 right-3 z-[1000] flex flex-col gap-2">
          <ToolButton title="확대" onClick={() => mapRef.current?.zoomIn()}><Plus className="size-4" /></ToolButton>
          <ToolButton title="축소" onClick={() => mapRef.current?.zoomOut()}><Minus className="size-4" /></ToolButton>
          <div className="h-1" />
          <ToolButton title="마커 필터" active={openPanel === 'filter'} onClick={() => setOpenPanel(p => (p === 'filter' ? null : 'filter'))}>
            <Filter className="size-4" />
          </ToolButton>
          <ToolButton
            title="지역 선택"
            active={showRegionOverlay}
            onClick={() => {
              if (showRegionOverlay) {
                clearRegionOverlay();
              } else {
                setShowRegionOverlay(true);
                setShowHeatmap(false);
                setOpenPanel(null);
              }
            }}
          >
            <MapPinned className="size-4" />
          </ToolButton>
          <ToolButton title="통계 오버레이" active={showStats} onClick={toggleStats}><BarChart3 className="size-4" /></ToolButton>
          <ToolButton title="가격 시각화" active={openPanel === 'price'} onClick={() => setOpenPanel(p => (p === 'price' ? null : 'price'))}>
            <TrendingUp className="size-4" />
          </ToolButton>
          <ToolButton title="매장 매출" active={openPanel === 'sales' || showSales} onClick={() => setOpenPanel(p => (p === 'sales' ? null : 'sales'))}>
            <CircleDollarSign className="size-4" />
          </ToolButton>
        </div>

        <div className="absolute top-4 right-14 z-[1000] w-52">
          {openPanel === 'stats' && showStats && (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 w-52">
              <div className="text-xs font-semibold text-gray-500 mb-1.5">
                통계 지표 ({STATS_YEAR})
              </div>
              <Select value={statsMetric} onValueChange={(v) => setStatsMetric(v as MetricKey)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[1100]">
                  {METRIC_DEFS.map((m) => (
                    <SelectItem key={m.key} value={m.key} className="text-xs">
                      {m.label} ({m.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {openPanel === 'price' && (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 w-52 space-y-3">
              <div>
                <div className="text-xs font-semibold text-gray-500 mb-1.5">기준 유종</div>
                <Select value={priceFuel} onValueChange={v => setPriceFuel(v as FuelType)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[1100]">
                    {Object.entries(FUEL_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key} className="text-xs">{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                {[
                  { label: '마커 색 = 가격 (싸면 초록)', state: colorByPrice, toggle: () => setColorByPrice(v => !v) },
                  { label: '지역 평균가 히트맵', state: showHeatmap, toggle: () => { if (!showHeatmap) clearRegionOverlay(); setShowHeatmap(v => !v); } },
                  { label: '주변 대비 이상치 표시', state: showOutliers, toggle: () => setShowOutliers(v => !v) },
                  { label: '가격 타임라인 (30일)', state: timelineOn, toggle: () => setTimelineOn(v => !v) },
                ].map(({ label, state, toggle }) => (
                  <button
                    key={label}
                    className={`w-full text-left text-xs px-2 py-1 rounded border transition-colors ${
                      state
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                    }`}
                    onClick={toggle}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {openPanel === 'sales' && (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 w-52 space-y-3">
              <button
                className={`w-full text-left text-xs px-2 py-1 rounded border transition-colors ${
                  showSales
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                }`}
                onClick={() => setShowSales(v => !v)}
              >
                매출 비례 심볼
              </button>
              <div>
                <div className="text-xs font-semibold text-gray-500 mb-1.5">집계 월</div>
                <input
                  type="month"
                  value={salesMonth}
                  max={currentYearMonth}
                  onChange={e => { if (e.target.value) setSalesMonth(e.target.value); }}
                  className="h-8 w-full rounded-md border border-gray-300 px-2 text-xs"
                  aria-label="매출 집계 월"
                />
              </div>
              <p className="text-[11px] leading-snug text-gray-500">
                오피넷 ID가 등록된 내 주유소만 표시됩니다. 이번 달은 마감된 날짜까지의 부분 집계입니다.
              </p>
            </div>
          )}

          {openPanel === 'filter' && (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 w-52">
              {BRAND_CODES.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-gray-500 mb-1.5">브랜드</div>
                  <div className="flex flex-wrap gap-1">
                    {BRAND_CODES.map(code => (
                      <button
                        key={code}
                        className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                          filterBrands.includes(code)
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                        }`}
                        onClick={() => setFilterBrands(prev =>
                          prev.includes(code) ? prev.filter(b => b !== code) : [...prev, code]
                        )}
                      >
                        {POLL_CODE_BRAND_NAMES[code] ?? code}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs font-semibold text-gray-500 mb-1.5">조건</div>
                <div className="space-y-1">
                  {([
                    { label: '24시간 영업', state: filter24h, setter: setFilter24h },
                    { label: '세차장', state: filterCarWash, setter: setFilterCarWash },
                    { label: '편의점', state: filterConvenienceStore, setter: setFilterConvenienceStore },
                    { label: '정비소', state: filterMaintenance, setter: setFilterMaintenance },
                    { label: '셀프', state: filterSelf, setter: setFilterSelf },
                    { label: 'LPG 충전', state: filterLpg, setter: setFilterLpg },
                  ] as const).map(({ label, state, setter }) => (
                    <button
                      key={label}
                      className={`w-full text-left text-xs px-2 py-1 rounded border transition-colors ${
                        state
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                      }`}
                      onClick={() => setter(v => !v)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 사이드패널 */}
      <StationSidePanel
        selectedStation={selectedStation}
        stations={displayStations}
        outliers={outliers}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onStationSelect={handleStationSelect}
        onClose={toggleSidePanel}
        loading={loading}
        open={showSidePanel}
        expanded={sheetExpanded}
        onSwipe={handleSheetSwipe}
        myStores={myStores}
        myStoreCd={activeStore?.storeCd ?? ''}
        onMyStoreChange={setMyStoreCd}
      />

      {/* 선택된 주유소 상세 정보 플로팅 카드 */}
      {selectedStation && (
        <StationDetailCard
          station={selectedStation}
          onClose={() => setSelectedStation(null)}
        />
      )}
    </div>
  );
}
